import {
  apiV2ComprehensiveLabelListPost,
  apiV2DataMonitorAlarmRuleSelectableDevicePost,
  V2DataMonitorAlarmRuleSelectableDevicePostRequest,
} from '@maxtropy/device-customer-apis-v2';
import {
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  usePaging,
  Checkbox,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import React, { useEffect, useMemo, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';

interface SelectDeviceModalProps {
  open: boolean;
  ruleId?: number;
  deviceTypeId?: number;
  physicalModelId?: number;
  dataPropertyIds?: number[];
  boundDeviceIds?: number[];
  value?: number[];
  onChange: (value: number[]) => void;
  onClose: () => void;
  isEdit: boolean;
}

type DeviceSearchParams = Omit<
  V2DataMonitorAlarmRuleSelectableDevicePostRequest,
  'ruleId' | 'deviceCategoryId' | 'thingModelId' | 'dataPropertyIds' | 'page' | 'size'
>;

type DeviceSearchFormValues = Omit<DeviceSearchParams, 'isThirdParty'> & {
  isThirdParty?: boolean;
};

const SelectDeviceModal: React.FC<SelectDeviceModalProps> = ({
  open,
  ruleId,
  deviceTypeId,
  physicalModelId,
  dataPropertyIds,
  boundDeviceIds = [],
  value = [],
  onChange,
  onClose,
  isEdit,
}) => {
  const [form] = Form.useForm<DeviceSearchFormValues>();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<DeviceSearchParams>({});
  const [draftDeviceIds, setDraftDeviceIds] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      setDraftDeviceIds(value);
    }
  }, [open, value]);

  const { data: labelData } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }), {
    ready: open,
  });

  const labelOptions = useMemo(
    () =>
      (labelData?.list ?? []).flatMap(item =>
        item.id === undefined || !item.name ? [] : [{ label: item.name, value: item.id }]
      ),
    [labelData]
  );

  const {
    data: deviceList = [],
    loading,
    cancel,
  } = useRequest(
    () => {
      const requestParams: V2DataMonitorAlarmRuleSelectableDevicePostRequest = {
        ruleId,
        deviceCategoryId: deviceTypeId,
        thingModelId: physicalModelId,
        dataPropertyIds: dataPropertyIds?.length ? dataPropertyIds : undefined,
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      };

      return apiV2DataMonitorAlarmRuleSelectableDevicePost(requestParams).then(response => {
        setTotalCount(response.total ?? 0);
        return response.list ?? [];
      });
    },
    {
      ready: open && deviceTypeId !== undefined,
      refreshDeps: [
        open,
        ruleId,
        deviceTypeId,
        physicalModelId,
        dataPropertyIds,
        pageOffset,
        pageSize,
        searchParams,
        setTotalCount,
      ],
    }
  );

  const handleClose = () => {
    cancel();
    form.resetFields();
    setSearchParams({});
    setPageOffset(1);
    setDraftDeviceIds([]);
    onClose();
  };

  const handleConfirm = () => {
    onChange(draftDeviceIds);
    handleClose();
  };

  const columns = [
    {
      title: '设备编号',
      dataIndex: 'deviceCode',
      width: 180,
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      width: 180,
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '是否为三方设备',
      dataIndex: 'isThirdParty',
      width: 150,
      render: (value?: number) => <EllipsisSpan value={value === undefined ? '--' : value === 1 ? '是' : '否'} />,
    },
    {
      title: '所属类目',
      dataIndex: 'deviceTypeName',
      width: 180,
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '设备标签',
      dataIndex: 'deviceTags',
      width: 220,
      render: (value?: string[]) => <EllipsisSpan value={value?.length ? value.join('、') : '--'} />,
    },
    {
      title: '所属组织',
      dataIndex: 'organizationName',
      width: 180,
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
  ];

  const filters = (
    <CustomFilter<DeviceSearchFormValues>
      colSpan={6}
      form={form}
      onFinish={values => {
        const { isThirdParty, ...rest } = values as DeviceSearchFormValues;
        setSearchParams({ ...rest, isThirdParty: isThirdParty ? 1 : undefined });
        setPageOffset(1);
      }}
      onReset={() => {
        setSearchParams({});
        setPageOffset(1);
      }}
    >
      <Form.Item name="deviceCodeOrName" label="编号/名称">
        <Input allowClear placeholder="请输入" />
      </Form.Item>
      <Form.Item name="deviceTag" label="设备标签">
        <Select allowClear showSearch optionFilterProp="label" placeholder="请选择设备标签" options={labelOptions} />
      </Form.Item>
      <Form.Item name="isThirdParty" valuePropName="checked">
        <Checkbox>是否为三方设备</Checkbox>
      </Form.Item>
    </CustomFilter>
  );

  const persistedDeviceIds = [...new Set(boundDeviceIds)];
  const selectedRowKeys = [...new Set([...persistedDeviceIds, ...draftDeviceIds])];
  const totalSelectedCount = selectedRowKeys.length;

  return (
    <Modal
      open={open}
      title="关联设备"
      size="big"
      destroyOnClose
      okText="确定"
      cancelText="取消"
      onOk={handleConfirm}
      onCancel={handleClose}
    >
      {filters}
      {isEdit && (
        <div style={{ marginBottom: 10, textAlign: 'right' }}>
          <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} /> 可选设备需具备报警规则已选定的监测数据属性
        </div>
      )}
      <Table
        rowKey="deviceId"
        sticky
        scroll={{ x: 900, y: 350 }}
        loading={loading}
        columns={columns}
        dataSource={deviceList}
        rowSelection={{
          type: 'checkbox',
          preserveSelectedRowKeys: true,
          selectedRowKeys,
          getCheckboxProps: record => ({
            disabled: persistedDeviceIds.includes(Number(record.deviceId)),
          }),
          onChange: keys => {
            setDraftDeviceIds(
              keys.map(Number).filter(deviceId => Number.isFinite(deviceId) && !persistedDeviceIds.includes(deviceId))
            );
          },
        }}
        bottomLeftExtra={
          <div>
            <span>本次新增勾选 </span>
            <span style={{ color: 'var(--mx-error-color)' }}>{draftDeviceIds.length}</span>
            <span> 项，总计 </span>
            <span style={{ color: 'var(--mx-error-color)' }}>{totalSelectedCount}</span>
            <span> 项</span>
          </div>
        }
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default SelectDeviceModal;
